import _ from 'lodash';
import { useStore } from '@/store';
import { getKeys, getValues } from '@morph-mapper/utils';
import { EntryType } from '@morph-mapper/types';
import { P, match } from 'ts-pattern';
import Queue from 'queue-fifo';
import { useLogicBlocks, useStructureBlocks } from '@morph-mapper/node-logic';
import { usePlugins } from '@morph-mapper/plugins';
import { useDependencies } from './use-dependencies';
import { useEntryOverride } from './use-entry-override';
import {
  TreeNode,
  TreeItem,
  TreeMap,
  isTreeItem,
  isTreeMap,
  isZodNumber,
  SchemaVariant,
} from '@morph-mapper/types';
import { useSchemaEditor } from './use-schema-editor';
import { useRef } from 'react';
import { compileLogicTree } from '../components/graph-editor/lib';
import { createDependencyScanner, ScanMode } from '@/utils';
import { iteratorKeys } from '@/config';
import { useTemplate } from '../api/templates/query';

export const renderCellColumn = (column: number) => {
  const zeroBasedColumn = Math.max(0, Math.trunc(column) - 1);
  return `#>${zeroBasedColumn}$get`;
};

export const normalizeTemplateForEntryKey = (
  entryKey: string,
  template: any,
) => {
  if (!iteratorKeys.includes(entryKey)) {
    return template;
  }

  if (
    template !== null &&
    typeof template === 'object' &&
    !Array.isArray(template) &&
    entryKey in template
  ) {
    return (template as Record<string, any>)[entryKey];
  }

  return template;
};

export const getFinalResultKey = (entryId: string) =>
  `__reserved__result__${entryId}`;

export const replaceFinalResultKey = (entryId: string, template: any): any => {
  const sourceKey = `__reserved__key__${entryId}`;
  const resultKey = getFinalResultKey(entryId);

  if (Array.isArray(template)) {
    return template.map((value) => replaceFinalResultKey(entryId, value));
  }

  if (template !== null && typeof template === 'object') {
    return Object.fromEntries(
      Object.entries(template).map(([key, value]) => [
        key === sourceKey ? resultKey : key,
        replaceFinalResultKey(entryId, value),
      ]),
    );
  }

  if (typeof template === 'string') {
    return template.replaceAll(sourceKey, resultKey);
  }

  return template;
};

// XLS doesnt work when $data or $Array.forEach are inside eachother multiple times,
// unless the outer $data has the rows into groups.
export const flattenDoubleNestedScopes = (template: any): any => {
  if (Array.isArray(template)) {
    return template.map(flattenDoubleNestedScopes);
  }

  if (template === null || typeof template !== 'object') {
    return template;
  }

  const result: Record<string, any> = {};
  for (const [key, value] of Object.entries(template)) {
    result[key] = flattenDoubleNestedScopes(value);
  }

  const outerData = result['$data'];
  if (
    outerData &&
    typeof outerData === 'object' &&
    outerData['$Array.classify'] !== undefined
  ) {
    return result;
  }

  for (const scopeKey of ['$data', '$Array.forEach']) {
    const outer = result[scopeKey];
    if (
      outer &&
      typeof outer === 'object' &&
      !Array.isArray(outer) &&
      scopeKey in outer
    ) {
      const { [scopeKey]: inner, ...rest } = outer;
      result[scopeKey] = { ...rest, ...inner };
    }
  }

  return result;
};

export const useRenderTemplate = () => {
  const [templateId, variant, IEntry, getEntries, IGraph, getGraphTemplate] =
    useStore(({ entries: e, config: c, graphs: g }) => [
      c.getId(),
      c.getVariant(),
      e.entry,
      e.getEntries,
      g.graph,
      g.getGraphTemplate,
    ]);
  const targetEntry = useRef<string | undefined>(undefined);
  const targetNode = useRef<string | undefined>(undefined);
  const { structureBlocks } = useStructureBlocks(variant);
  const { logicBlocks } = useLogicBlocks(variant);
  const { retrieveDependencies } = useDependencies();
  const { override, setOverride, clearOverride } = useEntryOverride();
  const { shouldRenderField } = useSchemaEditor();
  const { data: tempTemplate } = useTemplate(templateId);
  const { shouldRender, postRender } = usePlugins();

  /**
   * Parses an entry item into a template object.
   *
   * @param entry Current entry to parse into the template object
   * @returns parsed value of the entry
   */
  const renderItemEntry = (entryId: string) => {
    const entry = getEntries()[entryId];

    return match(entry)
      .with({ type: EntryType.Boolean }, (entry) => entry.value)
      .with({ type: EntryType.Simple }, (entry) => entry.value)
      .with({ type: EntryType.Cell }, (entry) => {
        const { column } = entry;
        if (column === undefined) return undefined;
        return renderCellColumn(column);
      })
      .with({ type: EntryType.Graph }, (entry) => {
        const { graphId } = entry;
        if (graphId === undefined) {
          return undefined;
        }

        const compiledTemplate = compileLogicTree(
          IGraph(graphId).get(),
          logicBlocks,
          getGraphTemplate,
          targetNode.current,
        );

        return normalizeTemplateForEntryKey(entry.key, compiledTemplate);
      })
      .with({ type: EntryType.Internal }, (entry) => {
        if (typeof entry.value === 'string') {
          if (entry.value.charAt(0) === '{' || entry.value.charAt(0) === '[') {
            return JSON.parse(entry.value);
          }
          return entry.value;
        }

        return entry.value;
      })
      .with({ type: EntryType.Code }, (entry) => {
        return entry.value;
      })
      .with({ type: EntryType.Map }, (entry) => {
        if (entry.outputType === 'code') {
          return entry.value;
        } else {
          throw new Error('Cannot render map entry.');
        }
      })
      .exhaustive();
  };

  const getRoot = () => {
    const root = getValues(getEntries()).find((e) => e.parentId === undefined);
    if (!root) throw new Error('Root not found');

    return root.id;
  };

  const buildChildQueue = (childrenIds: string[], pointer: string[]) => {
    const snapshot = getEntries();

    return childrenIds.map((id) => {
      const entry = snapshot[id];
      return match(entry)
        .with({ type: EntryType.Map }, (entry) => {
          if (entry.outputType === 'code') {
            return { pointer: [...pointer, entry.key], entryId: id };
          } else {
            return { pointer, entryId: id };
          }
        })
        .otherwise(() => {
          return { pointer: [...pointer, entry.key], entryId: id };
        });
    });
  };

  const populateEdiVariables = (template: any) => {
    if (tempTemplate === undefined || tempTemplate.ediVariables === undefined) {
      return template;
    }

    const replacementMap: Record<string, string> = {};
    Object.values(tempTemplate.ediVariables).forEach((variable: any) => {
      replacementMap[variable.schemaVariable] = variable.value;
    });

    const recursiveReplace = (obj: any): any => {
      if (typeof obj === 'string') {
        return replacementMap[obj] || obj;
      } else if (Array.isArray(obj)) {
        return obj.map(recursiveReplace);
      } else if (obj !== null && typeof obj === 'object') {
        return Object.fromEntries(
          Object.entries(obj).map(([key, value]) => [
            key,
            recursiveReplace(value),
          ]),
        );
      }
      return obj;
    };

    return recursiveReplace(template);
  };

  /**
   * Determines if a number should be parsed as an integer or float.
   * This is a heuristic-based approach since we can't know the actual value at template build time.
   *
   * @param templateValue The template value (usually a string like "#||2#:")
   * @param entryKey The entry key name (for hints like "id", "count" vs "price", "amount")
   * @returns 'int' or 'float'
   */
  const determineNumberType = (
    templateValue: any,
    entryKey: string,
  ): 'int' | 'float' => {
    // Check entry key for hints
    const lowerKey = entryKey.toLowerCase();
    const floatIndicators = [
      'price',
      'amount',
      'cost',
      'weight',
      'total',
      'sum',
    ];

    if (floatIndicators.some((indicator) => lowerKey.includes(indicator))) {
      return 'float';
    }

    // Default to float as it can handle both integers and floats
    return 'int';
  };

  /**
   * Automatically wraps a template value with number parsing transformation
   * if the entry has z.Number() validation.
   *
   * @param entry The entry with validation schema
   * @param templateValue The template value to potentially wrap
   * @returns The wrapped value or original value
   */
  const applyAutoNumberTransformation = (
    entry: TreeItem,
    templateValue: any,
  ): any => {
    // Check if entry has z.Number() validation
    if (!isZodNumber(entry.validation)) {
      return templateValue;
    }

    // Skip if already an object - check if it's already wrapped in a transformation
    if (typeof templateValue === 'object' && templateValue !== null) {
      // Check if already has $math.parseInt or $math.parseFloat
      if (
        '$math.parseInt' in templateValue ||
        '$math.parseFloat' in templateValue
      ) {
        return templateValue;
      }
      // If it's already an object but not a transformation, don't wrap it
      return templateValue;
    }

    // Only apply transformation to string values (template references)
    if (typeof templateValue !== 'string') {
      return templateValue;
    }

    // Skip if it's already JSON (starts with { or [)
    if (templateValue.charAt(0) === '{' || templateValue.charAt(0) === '[') {
      return templateValue;
    }

    // Determine if we should use parseInt or parseFloat
    const numberType = determineNumberType(templateValue, entry.key);

    // Wrap with the appropriate transformation
    // Format: { "$math.parseInt": templateValue } or { "$math.parseFloat": templateValue }
    if (numberType === 'int') {
      return {
        '$math.parseInt': templateValue,
      };
    } else {
      return {
        '$math.parseFloat': templateValue,
      };
    }
  };

  const updateTemplate = (
    entryId: string,
    template: any,
    pointer: string[],
    registerMap?: (entry: TreeMap, objPointer: string[]) => void,
    registerItem?: (entry: TreeItem) => void,
  ) => {
    if (!shouldRenderField(entryId)) {
      return;
    }
    const snapshot = getEntries();
    if (!shouldRender({ entry: snapshot[entryId] })) {
      return;
    }

    const overridden = override(snapshot[entryId]);

    if (overridden.type === EntryType.Map && overridden.outputType === 'code') {
      // When type is map & code, treat it like an treeitem instead of treemap
      const entry = overridden as unknown as TreeItem;
      let value =
        typeof entry.value === 'string' &&
        (entry.value.startsWith('{') || entry.value.startsWith('['))
          ? JSON.parse(entry.value)
          : entry.value;

      // Automatically apply number transformation for z.Number() schemas
      value = applyAutoNumberTransformation(entry, value);

      _.set(template, pointer, postRender({ entry, template: value }));
      registerItem?.(entry);
      return;
    }

    match(overridden)
      .with(P.when(isTreeMap), (entry) => {
        const { key, outputType } = entry;
        const [objPointer, obj] = structureBlocks[outputType].render(key);

        match(_.get(template, pointer, undefined)).with(
          undefined,
          () => {
            _.set(template, pointer, obj);
          },
          (sibling) => {
            // TODO: do we want to check if we are overwriting parts of the template?
            // Currently might result in hard to debug issues.
            _.set(template, pointer, _.merge(sibling, obj));
          },
        );

        registerMap && registerMap(entry, objPointer);
      })
      .with(P.when(isTreeItem), (entry) => {
        // FIXME: this is a hotfix, but we should set the invariant sucht that the .value the actual template code
        // and not a string representation of the template code.
        let templateValue: any;
        if (typeof entry.value === 'string') {
          if (entry.value.charAt(0) === '{' || entry.value.charAt(0) === '[') {
            templateValue = JSON.parse(entry.value);
          } else {
            templateValue = entry.value;
          }
        } else {
          templateValue = entry.value;
        }

        templateValue = normalizeTemplateForEntryKey(entry.key, templateValue);

        // Automatically apply number transformation for z.Number() schemas
        templateValue = applyAutoNumberTransformation(entry, templateValue);

        _.set(
          template,
          pointer,
          postRender({ entry, template: templateValue }),
        );

        registerItem && registerItem(entry);
      })
      .exhaustive();
  };

  /**
   * Builds a template object for a specific entry.
   *
   * @returns A template object which can be parsed by the template engine.
   */
  const renderEntryWithContext = (
    entryId: string,
    nodeId?: string,
    templateOverride?: any,
    dependencyOverride?: string[],
    contextEntryId?: string,
  ) => {
    const targetValue = templateOverride ?? IEntry(entryId).getTemplateValue();
    if (targetValue === undefined) {
      return undefined;
    }

    targetEntry.current = entryId;
    targetNode.current = nodeId;

    const dependencyIds =
      dependencyOverride ?? retrieveDependencies(contextEntryId ?? entryId);
    const dependencyPaths = [...dependencyIds, entryId].reduce(
      (map, id) => map.set(id, IEntry(id).getPath()),
      new Map<string, string[]>(),
    );
    const template = {};
    const templatePointers = new Map<string, string[]>();

    const getPointer = (index: number, path: string[]) => {
      if (index === 0) {
        return [];
      }

      const pointer = templatePointers.get(path[index - 1]);
      if (pointer === undefined) {
        throw new Error('Cache is inconsistent');
      }

      return pointer;
    };

    dependencyPaths.forEach((dependencyPath, dependencyId) => {
      dependencyPath.forEach((entryId, index) => {
        if (templatePointers.has(entryId)) {
          return;
        }
        const entry = getEntries()[entryId];
        const pointer = match(entry)
          .with({ type: EntryType.Map }, (entry) => {
            if (entry.outputType === 'code') {
              return [...getPointer(index, dependencyPath), entry.key];
            } else {
              return getPointer(index, dependencyPath);
            }
          })
          .otherwise(() => {
            return [...getPointer(index, dependencyPath), entry.key];
          });

        updateTemplate(
          entryId,
          template,
          pointer,
          (_, objPointer) => {
            templatePointers.set(entryId, [...pointer, ...objPointer]);
          },
          () => {
            templatePointers.set(entryId, pointer);
          },
        );
      });
    });

    // Resolve 'hiding' logic.
    const targetPointer = templatePointers.get(entryId);
    if (targetPointer === undefined) {
      return undefined;
    }

    try {
      exposeVariableForOutput(entryId, template, targetPointer);

      // Add lastObtain position in BOTH the old position (declaration) and new position (reference)
      const referencePath = findReservedKeyReferencePath(template, entryId);
      const declarationPath = findReservedKeyDeclarationPath(template, entryId);

      // Set position on the parent of the reference path (new position)
      if (referencePath) {
        const parentPath = referencePath.slice(0, -1);
        const targetObj =
          parentPath.length === 0 ? template : _.get(template, parentPath);
        if (targetObj && typeof targetObj === 'object') {
          targetObj.position = '#lastObtain';
        }
      }

      // Set position on the parent of the declaration path (old position)
      // But skip if the declaration is inside $declareVariable - set position on parent of $declareVariable instead
      if (declarationPath) {
        let parentPath = declarationPath.slice(0, -1);

        // If the parent is $declareVariable, go up one more level to avoid setting position inside $declareVariable
        if (
          parentPath.length > 0 &&
          parentPath[parentPath.length - 1] === '$declareVariable'
        ) {
          parentPath = parentPath.slice(0, -1);
        }

        const targetObj =
          parentPath.length === 0 ? template : _.get(template, parentPath);
        if (targetObj && typeof targetObj === 'object') {
          targetObj.position = '#lastObtain';
        }
      }

      // Fallback: if neither path was found, add position to template root
      if (!referencePath && !declarationPath) {
        (template as any).position = '#lastObtain';
      }

      return variant === SchemaVariant.Table
        ? flattenDoubleNestedScopes(template)
        : template;
    } catch {
      return undefined;
    }
  };

  const renderWithContext = (
    entryId: string,
    template: any,
    options?: { contextEntryId: string; forceItemRender: boolean },
  ) => {
    if (template === undefined) {
      return undefined;
    }

    const normalizedTemplate = normalizeTemplateForEntryKey(
      IEntry(entryId).getKey(),
      template,
    );
    const contextEntryId = options?.contextEntryId ?? entryId;
    const forceItemRender = options?.forceItemRender ?? false;

    let dependencyOverride: string[] | undefined;
    try {
      const snapshot = getEntries();
      const patchedEntries = {
        ...snapshot,
        [entryId]: {
          ...snapshot[entryId],
          value: normalizedTemplate,
          ...(forceItemRender ? { type: EntryType.Simple } : {}),
        },
      } as Record<string, TreeNode>;
      const { scanDependencies } = createDependencyScanner(patchedEntries);
      const scannedDependencies = scanDependencies(
        ScanMode.Singular,
        contextEntryId,
      );
      const dependencies = scannedDependencies.get(entryId);
      if (dependencies) {
        const resolvedDependencies = new Set<string>();
        // Check all dependencies recursively so we always know if it has to be referenced
        const queue = Array.from(dependencies.dependsOn);
        while (queue.length > 0) {
          const dependencyId = queue.shift()!;
          if (resolvedDependencies.has(dependencyId)) {
            continue;
          }

          resolvedDependencies.add(dependencyId);
          const scannedEntry = scannedDependencies.get(dependencyId);

          if (scannedEntry) {
            scannedEntry.dependsOn.forEach((id) => queue.push(id));
          } else {
            retrieveDependencies(dependencyId).forEach((id) =>
              resolvedDependencies.add(id),
            );
          }
        }
        dependencyOverride = Array.from(resolvedDependencies).reverse();
      }
    } catch {
      dependencyOverride = undefined;
    }

    if (forceItemRender) {
      setOverride(entryId, 'type', EntryType.Simple);
    }
    setOverride(entryId, 'value', normalizedTemplate);
    const result = renderEntryWithContext(
      entryId,
      undefined,
      normalizedTemplate,
      dependencyOverride,
      contextEntryId,
    );
    clearOverride(entryId);
    return replaceFinalResultKey(entryId, result);
  };

  /**
   * Builds a template object from the template tree.
   *
   * @returns A template object which can be parsed by the template engine.
   */
  const renderTemplate = (fromNodeId?: string) => {
    const template: Record<string, any> = {};
    const entryId = fromNodeId ?? getRoot();

    const queue = new Queue<{ pointer: string[]; entryId: string }>();
    queue.enqueue({ pointer: [], entryId });

    while (queue.isEmpty() === false) {
      const { pointer, entryId } = queue.dequeue()!;

      updateTemplate(entryId, template, pointer, ({ children }, objPointer) => {
        buildChildQueue(getKeys(children.forwardMap), [
          ...pointer,
          ...objPointer,
        ]).map((child) => queue.enqueue(child));
      });
    }

    const finalTemplate = populateEdiVariables(template);
    return variant === SchemaVariant.Table
      ? flattenDoubleNestedScopes(finalTemplate)
      : finalTemplate;
  };

  return {
    renderEntryWithContext,
    renderWithContext,
    renderTemplate,
    renderItemEntry,
  };
};

/**
 * Finds the common parent path of two paths
 * Returns the longest path that is a prefix of both input paths
 */
const findCommonParentPath = (path1: string[], path2: string[]): string[] => {
  const minLength = Math.min(path1.length, path2.length);
  const commonPath: string[] = [];

  for (let i = 0; i < minLength; i++) {
    if (path1[i] === path2[i]) {
      commonPath.push(path1[i]);
    } else {
      break;
    }
  }

  return commonPath;
};

/**
 * Recursively finds the path to the __reserved__key__${entryId} in the template object
 */
const findReservedKeyPath = (
  template: any,
  entryId: string,
  currentPath: string[] = [],
): string[] | null => {
  if (typeof template !== 'object' || template === null) {
    return null;
  }

  for (const [key, value] of Object.entries(template)) {
    const newPath = [...currentPath, key];

    // Check if this is the reserved key we're looking for
    if (key === `__reserved__key__${entryId}`) {
      return newPath;
    }

    // Recursively search in nested objects
    if (typeof value === 'object' && value !== null) {
      const result = findReservedKeyPath(value, entryId, newPath);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

/**
 * Recursively finds the path to the __reserved__key__${entryId} where it's used as a reference
 * Prioritizes finding $data.__reserved__key__${entryId} (in $Array.forEach) over $__reserved__key__${entryId} (in $data)
 */
const findReservedKeyReferencePath = (
  template: any,
  entryId: string,
  currentPath: string[] = [],
): string[] | null => {
  if (typeof template !== 'object' || template === null) {
    return null;
  }

  for (const [key, value] of Object.entries(template)) {
    const newPath = [...currentPath, key];

    // Check if this is the reserved key with a reference value
    if (key === `__reserved__key__${entryId}` && typeof value === 'string') {
      // Prioritize $data.__reserved__key__${entryId} (in $Array.forEach)
      if (value === `$data.__reserved__key__${entryId}`) {
        return newPath;
      }
      // For $__reserved__key__${entryId}, only return if not in $data context (to avoid the one in $data.$data)
      if (
        value === `$__reserved__key__${entryId}` &&
        !currentPath.includes('$data')
      ) {
        return newPath;
      }
    }

    // Recursively search in nested objects
    if (typeof value === 'object' && value !== null) {
      const result = findReservedKeyReferencePath(value, entryId, newPath);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

/**
 * Recursively finds the path to the __reserved__key__${entryId} where it's declared (not referenced)
 * This is the one that doesn't have a reference value (doesn't start with $)
 */
const findReservedKeyDeclarationPath = (
  template: any,
  entryId: string,
  currentPath: string[] = [],
): string[] | null => {
  if (typeof template !== 'object' || template === null) {
    return null;
  }

  for (const [key, value] of Object.entries(template)) {
    const newPath = [...currentPath, key];

    // Check if this is the reserved key that is NOT a reference (value doesn't start with $)
    if (
      key === `__reserved__key__${entryId}` &&
      (typeof value !== 'string' || !value.startsWith('$'))
    ) {
      return newPath;
    }

    // Recursively search in nested objects
    if (typeof value === 'object' && value !== null) {
      const result = findReservedKeyDeclarationPath(value, entryId, newPath);
      if (result) {
        return result;
      }
    }
  }

  return null;
};

const replaceTargetKey = (
  entryId: string,
  template: any,
  targetPath: string[],
  key: string,
) => {
  const newPath = match(key)
    .with(P.string.regex(/^\$/), () => {
      throw new Error(`Cannot determine render output target for ${key}.`);
    })
    .with(P.string.regex(/^#/), (key) => {
      if (!iteratorKeys.includes(key)) {
        throw new Error(`Cannot determine render output target for ${key}.`);
      }

      const newPath = [
        ...targetPath.slice(0, -1),
        `__reserved__key__${entryId}`,
      ];
      const obj = _.get(template, targetPath);
      _.set(template, [...newPath, key], obj);
      _.unset(template, targetPath);

      return newPath;
    })
    .otherwise(() => {
      const newPath = [
        ...targetPath.slice(0, -1),
        `__reserved__key__${entryId}`,
      ];
      const obj = _.get(template, targetPath);
      _.set(template, newPath, obj);
      _.unset(template, targetPath);

      return newPath;
    });

  return newPath;
};

/**
 * Exposes the target entry on the same level as the parent.
 * - When the parent exhibits non-hiding behaviour we don't have to do
 *  anything to expose the entry and return the path with the parent removed from the path.
 *
 * - When the parent exhibits hiding behaviour we inject template code to based on the parents functionality,
 *  we return the path which points to the currently exposed variable as defined in the injected template code.
 *
 * @returns the path which points to the parent of the highest level the entry is currently exposed on.
 */
const exposeFromParent = (
  entryId: string,
  template: any,
  path: string[],
  segments: string[],
): [string[], string | undefined] => {
  const updateObject = (path: string[], exposer: any) => {
    const targetReference = match(path.length)
      .with(0, () => template)
      .otherwise(() => _.get(template, path));

    if (targetReference === undefined) {
      _.set(template, path, exposer);
    } else {
      _.merge(targetReference, exposer);
    }
  };

  return match(path[path.length - 1])
    .with(P.string.regex(/^\$/), (key) => {
      return match(key)
        .with('$data', () => {
          const newPath = [...path];

          // Iterate from the end to the start of the array
          for (let i = newPath.length - 1; i >= 0; i--) {
            if (newPath[i] === '$data') {
              // Replace '$data' with '$Array.forEach'
              newPath[i] = '$Array.forEach';
            } else {
              // Stop replacing if the current element is not '$data'
              break;
            }
          }
          const exposer = {
            [`__reserved__key__${entryId}`]: `$data.__reserved__key__${entryId}`,
          };

          updateObject(newPath, exposer);
          return [newPath, undefined];
        })
        .with('$declareVariable', () => {
          const newPath = path.slice(0, -1);

          let internalPath = segments.reverse();
          if (
            internalPath[internalPath.length - 1] ===
            `__reserved__key__${entryId}`
          ) {
            internalPath = internalPath.slice(0, -1);
          }

          internalPath.push(`__reserved__key__${entryId}`);

          const exposer = {
            [`__reserved__key__${entryId}`]: `$${internalPath.join('.')}`,
          };

          updateObject(newPath, exposer);
          return [newPath, undefined];
        })
        .otherwise(() => {
          // TODO: determine all functional nodes which do still generate an output.
          if (['$Array.forEach'].includes(key)) {
            return [path.slice(0, -1), undefined];
          }
          throw new Error(`Cannot determine render output target for ${key}.`);
        });
    })
    .with(P.string.regex(/^#/), (key) => {
      throw new Error(
        `Cannot determine output, found key ${key} in parent path.`,
      );
    })
    .otherwise(() => {
      return [path.slice(0, -1), path[path.length - 1]];
    }) as [string[], string | undefined];
};

export const exposeVariableForOutput = (
  entryId: string,
  template: any,
  targetPath: string[],
) => {
  const targetKey = targetPath[targetPath.length - 1];
  let path = replaceTargetKey(entryId, template, targetPath, targetKey);
  let segments = [];

  while (path.length > 0) {
    const [newPath, segment] = exposeFromParent(
      entryId,
      template,
      path,
      segments,
    );
    path = newPath;

    // If there is no segment we performed an expose operation,
    // this makes all previous segments irrelevant. We reset the segments,
    // otherwise we add the segment to the list of segments (which is a reversed path).
    if (segment === undefined) {
      segments = [];
    } else {
      segments.push(segment);
    }
  }

  return template;
};
